import { Action, createReducer, on } from '@ngrx/store';

import { Slot, Service, SelectedEmployee, Appointment } from './appointment.models';
import {
  BookingScheduleAppointmentActions,
  BookingConfirmAppointmentActions,
  BookingScheduleAppointmentApiActions,
  BookingAppointmentActions,
  BookingAppointmentApiActions,
  BookingConfirmAppointmentApiActions,
} from './appointment.actions';
import { ANY_EMPLOYEE_MOCK } from '@mocks';
import { addDaysForDate, reformatDateUtils } from '@modules/core/utils/common';
import { PaymentOptionsEnum } from '@modules/core/enums';

export interface AppointmentState {
  entity: Appointment | null;
  startDate: string;
  endDate: string;
  startedAt: string;
  slots: Array<Slot>;
  services: Array<Service>;
  selectedEmployees: Array<SelectedEmployee>;
  loading: boolean;
  paymentMethod: PaymentOptionsEnum;
}

const initialState: AppointmentState = {
  entity: null,
  startDate: reformatDateUtils(new Date()),
  endDate: reformatDateUtils(new Date()),
  startedAt: '',
  slots: [],
  services: [],
  selectedEmployees: [],
  loading: false,
  paymentMethod: PaymentOptionsEnum.ONLINE,
};

const appointmentReducer = createReducer(
  initialState,
  on(
    BookingScheduleAppointmentActions.loadAvailableSlots,
    (state): AppointmentState => ({
      ...state,
      loading: true,
    })
  ),
  on(BookingScheduleAppointmentActions.setDefaultEmployee, (state, { checkedServices }): AppointmentState => {
    const defaultServices: Array<Service> = [];
    const defaultEmployee = ANY_EMPLOYEE_MOCK;

    checkedServices.forEach((item) => {
      defaultServices.push({
        uuid: item,
        employees: [defaultEmployee],
      });
    });

    return {
      ...state,
      services: [...defaultServices],
    };
  }),
  on(BookingScheduleAppointmentActions.updateSelectedEmployees, (state): AppointmentState => {
    const selectedEmployees: Array<SelectedEmployee> = [];

    state.services.forEach((item) => {
      const selectedEmployee = item.employees.find((employee) => employee.selected);
      if (selectedEmployee) {
        selectedEmployees.push({
          serviceUuid: item.uuid,
          employeeUuid: selectedEmployee.uuid,
        });
      } else {
        selectedEmployees.push({
          serviceUuid: item.uuid,
        });
      }
    });

    return {
      ...state,
      selectedEmployees: [...selectedEmployees],
    };
  }),
  on(BookingScheduleAppointmentActions.selectEmployee, (state, { serviceUuid, employeeUuid }): AppointmentState => {
    const selectedEmployee: SelectedEmployee = {
      serviceUuid,
      employeeUuid,
    };
    const copyArray = [...state.selectedEmployees];
    const serviceIndex = copyArray.findIndex((item) => item.serviceUuid === serviceUuid);

    copyArray[serviceIndex] = selectedEmployee;

    return {
      ...state,
      selectedEmployees: [...copyArray],
    };
  }),

  on(BookingConfirmAppointmentActions.setExternalNote, (state, { externalNote }): AppointmentState => {
    const entity = Object.assign(
      {},
      {
        ...state.entity,
        externalNote,
      }
    ) as Appointment;

    return {
      ...state,
      entity: { ...entity },
    };
  }),

  on(
    BookingScheduleAppointmentActions.setDate,
    (state, { date }): AppointmentState => ({
      ...state,
      startDate: date,
      endDate: addDaysForDate(new Date(date), 7),
    })
  ),
  on(
    BookingScheduleAppointmentActions.checkAvailableSlot,
    (state, { slot }): AppointmentState => ({
      ...state,
      startedAt: slot,
    })
  ),
  on(
    BookingScheduleAppointmentActions.clearAvailableSlot,
    (state): AppointmentState => ({
      ...state,
      startedAt: '',
    })
  ),
  on(BookingScheduleAppointmentApiActions.loadSlotsSuccess, (state, { slots, services }): AppointmentState => {
    let copyServices = [...services];

    copyServices = copyServices.map((service) => {
      const newItem = Object.assign({}, service);
      const defaultEmployee = Object.assign({}, ANY_EMPLOYEE_MOCK);

      defaultEmployee.selected = state.selectedEmployees.some(
        (selectedEmployee) => service.uuid === selectedEmployee.serviceUuid && selectedEmployee.employeeUuid === '-1'
      );

      if (service.employees.length > 1) {
        newItem.employees = [defaultEmployee, ...service.employees];
      }

      return newItem;
    });

    return {
      ...state,
      slots: [...slots],
      services: [...copyServices],
      loading: false,
    };
  }),
  on(
    BookingScheduleAppointmentApiActions.loadSlotsFailure,
    (state): AppointmentState => ({
      ...state,
    })
  ),
  // APPOINTMENT
  on(
    BookingAppointmentActions.loadAppointment,
    (state): AppointmentState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    BookingAppointmentApiActions.loadAppointmentSuccess,
    (state, { appointment }): AppointmentState => ({
      ...state,
      loading: false,
      entity: appointment,
    })
  ),
  on(
    BookingAppointmentApiActions.loadAppointmentFailure,
    (state): AppointmentState => ({
      ...state,
      loading: false,
    })
  ),
  // CONTACT DETAILS
  on(
    BookingConfirmAppointmentActions.activateAppointment,
    (state): AppointmentState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    BookingConfirmAppointmentApiActions.activateAppointmentSuccess,
    (state, { appointment }): AppointmentState => ({
      ...state,
      entity: appointment,
      loading: false,
    })
  ),
  on(
    BookingScheduleAppointmentApiActions.createDraftAppointmentSuccess,
    (state, { appointment }): AppointmentState => ({
      ...state,
      entity: appointment,
      loading: false,
    })
  ),
  on(
    BookingConfirmAppointmentApiActions.activateAppointmentFailure,
    (state): AppointmentState => ({
      ...state,
      loading: false,
    })
  ),
  on(
    BookingScheduleAppointmentApiActions.createDraftAppointmentFailure,
    (state): AppointmentState => ({
      ...state,
    })
  ),
  on(BookingConfirmAppointmentActions.setDefaultPaymentMethod, (state, { paymentProvider }): AppointmentState => {
    // todo: change after implementation all providers, now only adyen
    const isActivatedPaymentProvider = paymentProvider?.handle === 'adyen';
    return {
      ...state,
      paymentMethod: isActivatedPaymentProvider ? PaymentOptionsEnum.ONLINE : PaymentOptionsEnum.ON_SITE,
      loading: false,
    };
  }),
  on(BookingConfirmAppointmentActions.updatePaymentMethod, (state, { paymentMethod }): AppointmentState => {
    return {
      ...state,
      paymentMethod: paymentMethod,
      loading: false,
    };
  })
);

export function reducer(state: AppointmentState | undefined, action: Action) {
  return appointmentReducer(state, action);
}
